import { useCallback, useEffect, useState } from 'react';
import { GetPremierPartnerGroups, GetSubGroups, GetSites } from './_api';
import PremierPartnerGroupSingleSelect from './components/PremierPartnerGroupSingleSelect/PremierPartnerGroupSingleSelect';
import SubGroupOrSiteAccessYesNo from './components/SubGroupOrSiteAccessYesNo/SubGroupOrSiteAccessYesNo';
import PremierPartnerGroupForm from './classes/PremierPartnerGroupForm';
import SubgroupMultiSelect from './components/SubgroupMultiSelect/SubgroupMultiSelect';
import SiteMultiSelect from './components/SiteMultiSelect/SiteMultiSelect';

const HealthPlanetPPG = props => {
	const [validationErrors, setValidationErrors] = useState([]);
	const [premierPartnerGroupOptions, setPremierPartnerGroupOptions] =
		useState([]);
	const [subGroupOptions, setSubgroupOptions] = useState([]);
	const [siteOptions, setSiteOptions] = useState([]);
	const [eventObject, setEventObject] = useState({
		target: {
			id: props.name,
			name: props.name,
			value: '',
		},
	});
	const [touchedFields, setTouchedFields] = useState([]);

	/** Form Object */
	const [formState, setFormState] = useState(new PremierPartnerGroupForm());

	useEffect(() => {
		//Get Premier Partner group options
		GetPremierPartnerGroups().then(data => {
			//extract group names from response
			const groupNames = data.map(option => {
				return option.GroupName;
			});

			setPremierPartnerGroupOptions(groupNames);
		});
	}, []);

	useEffect(() => {
		const validationErrors = formState.Validate(touchedFields);
		setValidationErrors(validationErrors);
		setEventObject({
			target: {
				id: props.name,
				name: props.name,
				value: formatValue(formState),
			},
		});
	}, [formState, props.name, touchedFields]);

	//update event object to be passed to props.changeHandler(). if validation errors present set value to empty string
	useEffect(() => {
		if (validationErrors.length === 0) {
			props.changeHandler(eventObject);
		} else {
			let clearedValue = {
				target: {
					id: props.name,
					name: props.name,
					value: '',
				},
			};
			props.changeHandler(clearedValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventObject, validationErrors]);

	/** @param value @type PremierParnerGroupForm
	 * formats state object into human readable string
	 */
	const formatValue = value => {
		let result = '';
		//format Premier Partner Group
		let premierPartnerGroup =
			'Premier Partner Group: ' + value.premierPartnerGroup;
		result = result + premierPartnerGroup + ' ~ ';
		//format subgroups
		if (value.subgroups.length > 0) {
			let subGroups = 'Subgroups: ';
			for (const sg of value.subgroups) {
				subGroups = subGroups + sg.SubGroupName + ', ';
			}
			result = result + subGroups.slice(0, -2) + ' ~ '; //remove trailing comma
		}
		//format sites
		if (value.sites.length > 0) {
			let sites = 'Sites: ';
			for (const site of value.sites) {
				sites = sites + site.SiteName + ', ';
			}
			result = result + sites.slice(0, -2); //remove trailing comma
		}

		return result;
	};

	const handlePremierPartnerGroupChange = useCallback(
		event => {
		//update premier partner group value, reset site and subgroup
		const newState = new PremierPartnerGroupForm({
			premierPartnerGroup: event.target.value,
			requiresSubgroupOrSiteAccess: 'No',
			subgroups: [],
			sites: [],
		});

		//update Subgroup options
		GetSubGroups(newState.premierPartnerGroup).then(data => {
			setSubgroupOptions(data);
		});

		//get site options
		GetSites(newState.premierPartnerGroup).then(data => {
			setSiteOptions(data);
		});

			//add premierPartnerGroup to touched fields if not already present
			if (!touchedFields.includes('premierPartnerGroup')) {
				let updatedTouchedFields = [...touchedFields];
				updatedTouchedFields.push('premierPartnerGroup');
				setTouchedFields(updatedTouchedFields);
			}

		setFormState(newState);
		},
		[touchedFields]
	);

	const handleSubGroupOrSiteAccessChange = useCallback(
		event => {
			const newState = new PremierPartnerGroupForm({
				...formState,
				requiresSubgroupOrSiteAccess: event.target.value,
			});
			setFormState(newState);
		},
		[formState]
	);

	const handleSubGroupChange = useCallback(
		newOptions => {
			let selectedOptions = newOptions.filter(option => {
				return option.isSelected;
			});

			let updatedSubgroups = subGroupOptions.filter(subGroup => {
				//if subgroup.SubgroupName is present in selectedOptions.value, return true
				return selectedOptions
					.map(option => option.value)
					.includes(subGroup.SubGroupName);
			});

			const newState = new PremierPartnerGroupForm({
				...formState,
				subgroups: updatedSubgroups,
			});

			setFormState(newState);
		},
		[formState, subGroupOptions]
	);

	const handleSiteChange = useCallback(
		newOptions => {
			let selectedOptions = newOptions.filter(option => {
				return option.isSelected;
			});

			let updatedSites = siteOptions.filter(site => {
				//if site.SiteName is present in selectedOptions.value, return true
				return selectedOptions
					.map(option => option.value)
					.includes(site.SiteName);
			});

			const newState = new PremierPartnerGroupForm({
				...formState,
				sites: updatedSites,
			});

			setFormState(newState);
		},
		[formState, siteOptions]
	);

	return (
		<>
			<PremierPartnerGroupSingleSelect
				options={premierPartnerGroupOptions}
				handleChange={handlePremierPartnerGroupChange}
				validationError={validationErrors.filter(
					error => error.fieldName === 'premierPartnerGroup'
				)}
			/>
			{formState.premierPartnerGroup !== null &&
			formState.premierPartnerGroup !== '' ? (
				<SubGroupOrSiteAccessYesNo
					handleChange={handleSubGroupOrSiteAccessChange}
					value={formState.requiresSubgroupOrSiteAccess}
				/>
			) : null}
			{formState.requiresSubgroupOrSiteAccess === 'Yes' ? (
				<SubgroupMultiSelect
					handleChange={handleSubGroupChange}
					selectedOptions={formState.subgroups}
					options={subGroupOptions}
				/>
			) : null}
			{formState.requiresSubgroupOrSiteAccess === 'Yes' ? (
				<SiteMultiSelect
					handleChange={handleSiteChange}
					selectedOptions={formState.sites}
					options={siteOptions}
				/>
			) : null}
		</>
	);
};

export default HealthPlanetPPG;
